import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import {
  FETCH_JOB_ACTION,
  FETCH_PROFESSIONAL_PROFILE,
  fetchJob,
  fetchProfessionalProfile,
  getAllPaymnets,
} from '~/actions';
import { Screen } from '@tempmee/react';
import { BackButton, Box, CardSpinner, Column } from '~/commonComponents';
import { useLoading } from '~/hooks';
import { useAppSelector } from '~/hooks/useAppSelector';
import { useGetAuthorizationProfessional } from '~/domain/Credentials/usecases/useGetAuthorizationProfessional';
import { BadgesCard } from './components/BadgesCard/BadgesCard';
import { ProfileCard } from './components/ProfileCard/ProfileCard';

const actions = [FETCH_JOB_ACTION, FETCH_PROFESSIONAL_PROFILE];

export const ProfessionalProfile = () => {
  const hasFetchedPayment = useRef(false);
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isLoading } = useLoading(actions);

  const { jobId, professionalId } = params || {};

  const job = useAppSelector((state) => state.job.job);
  const professional = useAppSelector((state) => state.user.professionalProfile);
  const payments = useAppSelector((state) => state.payments.allPayments);

  const candidateId = professionalId || location?.state?.candidateId;
  const locationProfession = location?.state?.profession;
  const locationState = location?.state?.state;
  const locationSubProfession = location?.state?.subProfession;

  useEffect(() => {
    if (!payments?.length && !hasFetchedPayment.current) {
      dispatch(getAllPaymnets(job?.offer_owner?.id));
      hasFetchedPayment.current = true;
    }
  }, [dispatch, payments, job]);

  useEffect(() => {
    if (candidateId) {
      dispatch(
        fetchProfessionalProfile(
          candidateId,
          job?.offer_owner?.id,
          job?.profession || locationProfession,
          job?.subProfession || locationSubProfession,
          job?.state || locationState,
        ),
      );
    }
  }, [candidateId, job, dispatch, locationProfession, locationSubProfession, locationState]);

  useEffect(() => {
    if (jobId) {
      dispatch(fetchJob({ jobId }));
    }
  }, [jobId, dispatch]);

  const {
    displayProfession,
    displayProfessionName,
    displayLicenseNumber,
    isCredentialV2,
    authorizations,
  } = useGetAuthorizationProfessional({
    professionalId: candidateId ?? '',
    candidateLicense: professional?.license || professional?.licenses?.[0],
    jobProfessionName: job?.professionName,
    offerOwnerId: job?.offer_owner?.id,
    jobProfession: job?.profession ?? locationProfession,
    jobSubProfession: job?.subProfession ?? locationSubProfession,
    jobState: job?.state ?? locationState,
  });

  return (
    <Screen variant="standard">
      <Screen.Header
        start={<BackButton onClick={() => navigate(-1)} />}
        title="Professional Profile"
      />
      <Screen.Body inset>
        {isLoading || !professional ? (
          <CardSpinner />
        ) : (
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={12}>
            <ProfileCard
              professional={professional}
              job={jobId ? job : null}
              displayProfession={displayProfession}
              displayProfessionName={displayProfessionName}
              displayLicenseNumber={displayLicenseNumber}
              authorizations={authorizations}
              isCredentialV2={isCredentialV2}
            />
            <Column gap={12}>
              <BadgesCard professional={professional} />
            </Column>
          </Box>
        )}
      </Screen.Body>
    </Screen>
  );
};
